import React from 'react'
import { Box, Link, Space, TableColumn, TextBody } from '@ally/metronome-ui'
import { Row } from 'react-table'
import { maskAccountNumber } from '@ally/aos-utils'
import { Account as AfgAccount } from '@ally/data-types-afg-accounts'
import dayjs from 'dayjs'
import { formatBalance } from '../utils'
import { AFGAccountDetailsLink } from '../Shared/AccountDetailsLink'
import { DashboardView } from '../CustomizeDashboard/types'

function getAllyTM(account: AfgAccount): string {
  return `creditCard-creditCardAccount-${account.nickName || account.name}-Card`
}

interface GetColumnsProps {
  tableTypeHeader:
    | 'ALLY PLATINUM CREDIT CARD'
    | 'ALLY EVERYDAY CASH BACK CREDIT CARD'
    | 'ALLY UNLIMITED CASH BACK CREDIT CARD'
  view: DashboardView
  isLgDown: boolean
  allCCAccounts: AfgAccount[]
}

export const getColumns = ({
  tableTypeHeader,
  view,
  isLgDown,
  allCCAccounts,
}: GetColumnsProps): TableColumn<AfgAccount>[] => {
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown

  const columns = {
    accountName: {
      id: 'name',
      accessor: (account: AfgAccount): string => {
        return account.nickName || account.name
      },
      Header: tableTypeHeader,
      alignment: 'left',
      isSortable: true,
      Cell: ({
        row: { original: account },
      }: {
        row: Row<AfgAccount>
      }): React.ReactNode | string => {
        return (
          <>
            <AFGAccountDetailsLink
              text={account.nickName || account.name}
              account={account}
              key={account.id}
              allytm={getAllyTM(account)}
            />
            <Box display="inline-block" aria-hidden="true">
              {maskAccountNumber(account.accountNumber)}
            </Box>
          </>
        )
      },
    },
    currentBalance: {
      id: 'currentBalance',
      accessor: 'balance.statement',
      isSortable: true,
      Header: 'CURRENT BALANCE',
      alignment: 'right',
      Cell: ({
        row: { original: account },
      }: {
        row: Row<AfgAccount>
      }): React.ReactNode => {
        const currentBalance = account.balance?.statement ?? 0
        return formatBalance(currentBalance)
      },
    },
    availableCredit: {
      id: 'availableCredit',
      accessor: 'meta.availableCredit',
      isSortable: true,
      Header: 'AVAILABLE CREDIT',
      alignment: 'right',
      Cell: ({
        row: { original: account },
      }: {
        row: Row<AfgAccount>
      }): React.ReactNode => {
        const availableCredit = account.meta?.availableCredit ?? 0
        return formatBalance(availableCredit)
      },
    },
    paymentDueDate: {
      id: 'paymentDueDate',
      accessor: 'meta.paymentDueDate',
      Header: isDefaultView ? 'PAYMENT DUE DATE' : 'DUE DATE',
      alignment: 'right',
      isSortable: true,
      Cell: ({
        row: { original: account },
      }: {
        row: Row<AfgAccount>
      }): React.ReactNode | string => {
        if (!account.meta?.paymentDueDate) {
          // TODO: find product requirement for when this wouldn't be set
          return ''
        }
        const date = new Date(account.meta?.paymentDueDate)
        // API is sending us a UTC time of 00:00 and when localized it can unintentionally alter the day/month
        const [timeStrippedDueDate] = date.toISOString().split('T')
        const paymentDueDate = dayjs(timeStrippedDueDate).format('MMM DD, YYYY')
        return <span>{paymentDueDate}</span>
      },
    },
    actions: {
      id: 'actions',
      Header: 'ACTIONS',
      alignment: 'right',
      Cell: ({
        row: { original: account },
      }: {
        row: Row<AfgAccount>
      }): React.ReactElement<Element> => {
        if (account.meta?.paymentDue) {
          return (
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="flex-end"
              alignItems="center"
            >
              <TextBody tag="span" size="sm">
                {formatBalance(account.meta?.paymentDue)} min due
              </TextBody>
              <Space ml="xxs">
                {/* TODO: change link destination */}
                <Link
                  to="/dashboard"
                  text="Pay Card"
                  withAllyTM="Select to pay card"
                />
              </Space>
            </Box>
          )
        }
        return (
          // TODO: change link destination
          <Link
            to="/dashboard"
            text="Pay Card"
            withAllyTM="Select to pay card"
          />
        )
      },
    },
  }

  const tileViewCols = [
    columns.accountName,
    columns.currentBalance,
    columns.availableCredit,
    columns.actions,
  ] as TableColumn<AfgAccount>[]
  const defaultViewCols = [
    columns.accountName,
    columns.currentBalance,
    columns.availableCredit,
    columns.paymentDueDate,
    columns.actions,
  ] as TableColumn<AfgAccount>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
